/**
 * email-model — the per-language email design a message carries (REQ-112g).
 *
 * A design is a flat list of email-safe blocks + a small theme. The default
 * language's design lives on MessageItem.emailDesign; every other language's
 * on translations[tag].emailDesign (same pattern as the step-1 content model,
 * changelog/2026-07-30T2315). The design is the SOURCE; HTML is generated from
 * it at preview/send time by email-render.ts, and the plain-text alternative
 * is generated and written back as the language's email body.
 *
 * The block set is deliberately small and Outlook-survivable: everything here
 * renders as nested tables with inline styles. Do not add a block type without
 * adding its table renderer + plain-text line in email-render.ts.
 */

export interface EmailTheme {
  accent: string;
  bg: string;
  contentBg: string;
  text: string;
  /** Email-safe stack id from EMAIL_FONTS. */
  font: string;
  /** Emit prefers-color-scheme dark rules (operator decision 11). */
  darkAware: boolean;
}
import { blocksForTarget, blockHint } from "../../shared/block-registry";

export interface EmailBlock { uid: string; type: string; cfg: Record<string, unknown>; }
export interface EmailDesign { blocks: EmailBlock[]; theme: EmailTheme; }

/* ui-config — email font stacks. A stack, never a single font: a Windows face, its
   macOS equivalent, then a generic family, so every reader lands somewhere sane.
   `webFont` marks a font needing @font-face, which Outlook desktop and Gmail IGNORE
   — those readers get the fallback, which is why the picker says so out loud rather
   than implying universal support. ("Segoe UI" was removed: Windows/Office only, so
   every Mac and iPhone reader silently fell through it.) */
// ui-config — font family names (proper nouns, never translated); flagged to voyage in drop notes
export const EMAIL_FONTS = [
  { id: "arial", label: "Arial", stack: "Arial, Helvetica, sans-serif", group: "universal" as const },
  { id: "helvetica", label: "Helvetica", stack: "Helvetica, Arial, sans-serif", group: "universal" as const },
  { id: "verdana", label: "Verdana", stack: "Verdana, Geneva, sans-serif", group: "universal" as const },
  { id: "tahoma", label: "Tahoma", stack: "Tahoma, Geneva, Verdana, sans-serif", group: "universal" as const },
  { id: "trebuchet", label: "Trebuchet MS", stack: "'Trebuchet MS', 'Lucida Grande', Tahoma, sans-serif", group: "universal" as const },
  { id: "georgia", label: "Georgia", stack: "Georgia, 'Times New Roman', Times, serif", group: "universal" as const },
  { id: "times", label: "Times New Roman", stack: "'Times New Roman', Times, Georgia, serif", group: "universal" as const },
  { id: "palatino", label: "Palatino", stack: "'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif", group: "universal" as const },
  /* Brand — honoured by Apple Mail, iOS Mail and Thunderbird; falls back to Arial in
     Outlook desktop and Gmail. Roughly a third of a typical audience sees it. */
  { id: "dmsans", label: "DM Sans", stack: "'DM Sans', Arial, Helvetica, sans-serif", group: "brand" as const,
    webFont: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap" },
];
export type EmailFontGroup = "universal" | "brand";
export const emailFont = (id: string) => EMAIL_FONTS.find((f) => f.id === id) || EMAIL_FONTS[0];
export const emailFontStack = (id: string) => emailFont(id).stack;
/** The @font-face URL for a brand font, or null for a universal stack. */
export const emailFontWebUrl = (id: string): string | null => (emailFont(id) as { webFont?: string }).webFont || null;
/* ui-config — brand primary + safe darks (curated, not a free picker). */
export const EMAIL_ACCENTS = ["#5142C7", "#2D60EB", "#027D47", "#333333"];

let _seq = 0;
export const ebUid = () => `eb_${Date.now().toString(36)}${(_seq++).toString(36)}`;

/** The email palette, derived from the ONE block registry (stage 4). The list
 *  used to be written out here and again, differently, in nj-model — a block now
 *  declares its targets once and this reads the "email" slice. The renderer is
 *  untouched: email-render.ts stays the only thing that turns these into HTML,
 *  so output does not move. */
export const EMAIL_BLOCK_DEFS: { id: string; label: string; labelKey: string; hint: string }[] =
  blocksForTarget("email").map((b) => ({
    id: b.type,
    label: b.label,
    labelKey: b.labelKey ?? `messaging.email.block.${b.type}.label`,
    hint: blockHint(b, "email"),
  }));

/* ui-config — per-type defaults; placeholder copy, not tenant data. */
export function defaultEmailBlock(type: string, t: (k: string, f?: string) => string = (_k, f) => f ?? _k): EmailBlock {
  const cfg: Record<string, unknown> =
    type === "logo" ? { text: "Your brand", imageUrl: "", align: "left" }
    : type === "heading" ? { text: "Headline goes here", size: "lg", align: "left" }
    : type === "text" ? { text: "Write your paragraph here. Personalisation tokens like {first_name} work in any block.", align: "left" }
    : type === "button" ? { label: t("messaging.email.defaultButtonLabel", "View details"), url: "https://", target: "url", token: "", align: "left" }
    : type === "image" ? { url: "", alt: t("messaging.email.defaultImageAlt", "Describe the image"), radius: true }
    : type === "columns" ? { leftTitle: "First point", leftText: "A short supporting line.", rightTitle: "Second point", rightText: "A short supporting line." }
    : type === "spacer" ? { height: 24 }
    : type === "footer" ? { address: "Company name · Street · City", unsubscribeLabel: "Unsubscribe", note: "You received this because you registered with us." }
    : type === "code" ? { html: "<!-- Paste an HTML fragment. Tables + inline styles travel best. -->" }
    /* Artefact and live-data blocks (CD prompt 2026-09-01 §6b). All four were
       already rendered server-side and missing only from the palette. */
    : type === "calendar_link" ? { label: t("messaging.email.defaultCalendarLabel", "Add to your calendar"), ics: true, apple: true, align: "left" }
    : type === "qr" ? { caption: t("messaging.email.defaultQrCaption", "Show this code when you arrive"), size: 160, align: "center" }
    : type === "attendee_qr" ? { caption: t("messaging.email.defaultAttendeeQrCaption", "One code per attendee"), showNames: true, size: 128, align: "center" }
    : type === "countdown" ? { label: t("messaging.email.defaultCountdownLabel", "Starts in"), align: "center" }
    : {};
  return { uid: ebUid(), type, cfg };
}

/** Default template for a message's first design — seeds from the composer's
 *  existing subject/plain body so nothing already written is lost. ui-config. */
export function defaultEmailDesign(seed: { subject?: string; body?: string }): EmailDesign {
  const heading = defaultEmailBlock("heading");
  if (seed.subject && seed.subject.trim()) heading.cfg.text = seed.subject.trim();
  const text = defaultEmailBlock("text");
  if (seed.body && seed.body.trim()) text.cfg.text = seed.body.trim();
  return {
    blocks: [defaultEmailBlock("logo"), heading, text, defaultEmailBlock("button"), defaultEmailBlock("divider"), defaultEmailBlock("footer")],
    theme: { accent: EMAIL_ACCENTS[0], bg: "#F4F5F7", contentBg: "#FFFFFF", text: "#333333", font: "arial", darkAware: true },
  };
}

export const cloneDesign = (d: EmailDesign): EmailDesign => JSON.parse(JSON.stringify(d));

/** Does the design carry any real content? Feeds the composer's completeness
 *  gate — a bare scaffold with the placeholder copy untouched still counts as
 *  content by intent (the user chose to design), so presence of blocks is it. */
export function emailHasContent(d?: EmailDesign | null): boolean {
  return !!(d && d.blocks && d.blocks.length > 0);
}
